import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { api } from "../lib/api";
import {
  Folder, FolderPlus, File as FileIcon,
  Upload, Download, Search, ChevronRight,
  Tag as TagIcon, FolderOpen, Home, RefreshCw, Package, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  humanSize, buildTree, expiryStatus, isPreviewable,
  FolderTree, FileCard, Modal, PreviewModal, FileDetailModal, SumCard,
} from "./documents/parts";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";

export default function Documents() {
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "admin";
  const canDelete = canWrite;

  const [folders, setFolders] = useState([]);
  const [rootFileCount, setRootFileCount] = useState(0);
  const [files, setFiles] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [expanded, setExpanded] = useState(new Set(["__root__"]));
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [newFolderModal, setNewFolderModal] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);

  const [detailModal, setDetailModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [versionsModal, setVersionsModal] = useState(null);
  const [renameModal, setRenameModal] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const fileInputRef = useRef(null);
  const versionInputRef = useRef(null);
  const [pendingVersionOf, setPendingVersionOf] = useState(null);

  const [summary, setSummary] = useState({ total_files: 0, total_folders: 0, total_size: 0, tags: [] });
  const [employees, setEmployees] = useState([]);
  const [expiringList, setExpiringList] = useState([]);

  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const isSearching = !!debouncedSearch || !!tagFilter;

  const tree = useMemo(() => buildTree(folders), [folders]);
  const currentFolder = useMemo(() => folders.find(f => f.id === currentFolderId), [folders, currentFolderId]);

  const breadcrumb = useMemo(() => {
    const chain = [];
    let cur = currentFolder;
    const seen = new Set();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      chain.unshift(cur);
      cur = folders.find(f => f.id === cur.parent_id);
    }
    return chain;
  }, [currentFolder, folders]);

  const loadFolders = useCallback(async () => {
    const { data } = await api.get("/documents/folders");
    setFolders(data.folders || []);
    setRootFileCount(data.root_file_count || 0);
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isSearching) {
        if (debouncedSearch) params.q = debouncedSearch;
        if (tagFilter) params.tag = tagFilter;
      } else {
        params.folder_id = currentFolderId || "";
      }
      const { data } = await api.get("/documents/files", { params });
      setFiles(data.files || []);
    } finally { setLoading(false); }
  }, [currentFolderId, debouncedSearch, tagFilter, isSearching]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/documents/summary");
      setSummary(data || {});
    } catch { /* noop */ }
  }, []);

  const loadExpiring = useCallback(async () => {
    try {
      const { data } = await api.get("/documents/expiring?days=45");
      setExpiringList(data?.files || []);
    } catch { /* noop */ }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await api.get("/employees?limit=500");
      setEmployees(Array.isArray(data) ? data : (data?.items || []));
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadFolders(); loadSummary(); loadExpiring(); loadEmployees(); }, [loadFolders, loadSummary, loadExpiring, loadEmployees]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const toggleFolder = (id) => {
    setExpanded(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const openNewFolder = (parent_id = null) => {
    setNewFolderModal({ parent_id });
    setNewFolderName("");
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) { toast.error("Nama folder wajib diisi"); return; }
    try {
      await api.post("/documents/folders", { name: newFolderName.trim(), parent_id: newFolderModal.parent_id });
      toast.success("Folder dibuat");
      setNewFolderModal(null);
      await loadFolders();
      await loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal buat folder"); }
  };

  const openRenameFolder = (folder) => { setRenameModal({ type: "folder", ...folder }); setRenameValue(folder.name); };
  const openRenameFile = (file) => { setRenameModal({ type: "file", ...file }); setRenameValue(file.original_filename); };

  const doRename = async () => {
    if (!renameValue.trim()) { toast.error("Nama tidak boleh kosong"); return; }
    try {
      if (renameModal.type === "folder") {
        await api.patch(`/documents/folders/${renameModal.id}`, { name: renameValue.trim() });
        await loadFolders();
      } else {
        await api.patch(`/documents/files/${renameModal.id}`, { original_filename: renameValue.trim() });
        await loadFiles();
      }
      toast.success("Berhasil diubah");
      setRenameModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal ubah nama"); }
  };

  const deleteFolder = async (folder) => {
    if (!confirm(`Hapus folder "${folder.name}" beserta semua isinya (${folder.file_count || 0} file, sub-folder)? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/documents/folders/${folder.id}`);
      toast.success("Folder & isi dihapus");
      if (currentFolderId === folder.id) setCurrentFolderId(folder.parent_id || null);
      await loadFolders();
      await loadFiles();
      await loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus folder"); }
  };

  const uploadFiles = async (fileList, opts = {}) => {
    if (!fileList || fileList.length === 0) return;
    setUploadBusy(true);
    let ok = 0, fail = 0;
    for (const f of fileList) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name}: >25MB, dilewati`); fail++; continue; }
      const fd = new FormData();
      fd.append("file", f);
      if (opts.folder_id !== undefined) fd.append("folder_id", opts.folder_id || "");
      else if (currentFolderId) fd.append("folder_id", currentFolderId);
      if (opts.parent_file_id) fd.append("parent_file_id", opts.parent_file_id);
      if (opts.tags) fd.append("tags", opts.tags);
      if (opts.expires_at) fd.append("expires_at", opts.expires_at);
      if (opts.related_employee_id) fd.append("related_employee_id", opts.related_employee_id);
      try {
        await api.post("/documents/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "gagal"}`);
        fail++;
      }
    }
    setUploadBusy(false);
    if (ok) toast.success(`${ok} file berhasil diunggah${fail ? `, ${fail} gagal` : ""}`);
    await loadFolders();
    await loadFiles();
    await loadSummary();
    await loadExpiring();
  };

  const handleFileInput = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleVersionInput = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length && pendingVersionOf) {
      await uploadFiles(files.slice(0, 1), { parent_file_id: pendingVersionOf.id });
      setPendingVersionOf(null);
      if (detailModal) {
        try {
          const { data } = await api.get(`/documents/files/${detailModal.id}/versions`);
          if (data.versions?.[0]) setDetailModal(data.versions[0]);
        } catch { /* noop */ }
      }
    }
    e.target.value = "";
  };

  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canWrite) { toast.error("Hanya superadmin/admin yang bisa upload"); return; }
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const deleteFile = async (f, allVersions = false) => {
    if (!confirm(`Hapus "${f.original_filename}"${allVersions ? " (semua versi)" : ""}?`)) return;
    try {
      await api.delete(`/documents/files/${f.id}`, { params: { all_versions: allVersions } });
      toast.success("File dihapus");
      setDetailModal(null); setVersionsModal(null);
      await loadFiles(); await loadSummary(); await loadFolders();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const toggleTag = async (f, tag) => {
    const cur = f.tags || [];
    const next = cur.includes(tag) ? cur.filter(t => t !== tag) : [...cur, tag];
    try {
      const { data } = await api.patch(`/documents/files/${f.id}`, { tags: next });
      setFiles(fs => fs.map(x => x.id === f.id ? data : x));
      if (detailModal?.id === f.id) setDetailModal(data);
      await loadExpiring();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal update tag"); }
  };

  const updateFileMeta = async (f, patch) => {
    try {
      const { data } = await api.patch(`/documents/files/${f.id}`, patch);
      setFiles(fs => fs.map(x => x.id === f.id ? data : x));
      if (detailModal?.id === f.id) setDetailModal(data);
      await loadExpiring();
      toast.success("Tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const downloadFile = async (f) => {
    try {
      const resp = await api.get(`/documents/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal download"); }
  };

  const openPreview = async (f) => {
    if (!isPreviewable(f.content_type)) { downloadFile(f); return; }
    try {
      const resp = await api.get(`/documents/files/${f.id}/download`, { params: { preview: true }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPreviewModal({ file: f, url });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal preview"); }
  };

  const closePreview = () => {
    if (previewModal?.url) URL.revokeObjectURL(previewModal.url);
    setPreviewModal(null);
  };

  const showVersions = async (f) => {
    try {
      const { data } = await api.get(`/documents/files/${f.id}/versions`);
      setVersionsModal({ file: f, versions: data.versions || [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal ambil versi"); }
  };

  const downloadFolderZip = async (folder) => {
    try {
      toast.info("Menyiapkan zip...");
      const resp = await api.get(`/documents/folders/${folder.id}/download-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${folder.name}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal download zip"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1600px]" data-testid="documents-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Dokumen</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">
            Manajemen Dokumen & File
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Simpan template, formulir, PKWT, dokumen perpajakan, dan file HR lainnya. Maks 25MB per file.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <>
              <button onClick={() => openNewFolder(currentFolderId)} data-testid="btn-new-folder"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-800 text-sm font-medium">
                <FolderPlus className="w-4 h-4" /> Folder Baru
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                disabled={uploadBusy}
                data-testid="btn-upload-file"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] disabled:opacity-60 text-white text-sm font-medium">
                <Upload className="w-4 h-4" /> {uploadBusy ? "Mengunggah..." : "Upload File"}
              </button>
              <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInput}
                accept="*/*" data-testid="input-file" />
              <input ref={versionInputRef} type="file" hidden onChange={handleVersionInput} data-testid="input-version" />
            </>
          )}
        </div>
      </header>

      {expiringList.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-4 flex items-start gap-3" data-testid="expiring-banner">
          <Bell className="w-5 h-5 text-amber-700 shrink-0 mt-0.5 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {expiringList.length} dokumen PKWT/Perpajakan mendekati kadaluarsa
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {expiringList.slice(0, 6).map(f => {
                const st = expiryStatus(f.expires_at);
                const clr = st?.color || "slate";
                return (
                  <button key={f.id} onClick={() => setDetailModal(f)}
                    data-testid={`expiring-item-${f.id}`}
                    className={`text-[11px] px-2 py-1 rounded-md bg-white border border-${clr}-300 text-${clr}-800 font-medium hover:shadow-sm`}>
                    {f.original_filename} · <b>{st?.label || f.expires_at}</b>
                  </button>
                );
              })}
              {expiringList.length > 6 && (
                <span className="text-[10px] px-2 py-1 rounded-md bg-white text-slate-500">
                  +{expiringList.length - 6} lainnya
                </span>
              )}
            </div>
            <p className="text-[10px] text-amber-700 mt-1">
              Bot Telegram akan otomatis ping supervisor & HR pada H-30, H-7, H-1.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5">
        <SumCard I={Folder} label="Total Folder" value={summary.total_folders || 0} tint="emerald" />
        <SumCard I={FileIcon} label="Total File" value={summary.total_files || 0} tint="violet" />
        <SumCard I={Package} label="Total Storage" value={humanSize(summary.total_size || 0)} tint="amber" />
        <SumCard I={TagIcon} label="Kategori Tag" value={(summary.tags || []).length} tint="rose" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            data-testid="search-input"
            placeholder="Cari nama file di semua folder..."
            className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}
          data-testid="tag-filter"
          className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua tag</option>
          {(summary.tags || []).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {isSearching && (
          <span className="text-[11px] px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">
            Search mode (semua folder)
          </span>
        )}
        <button onClick={() => { loadFolders(); loadFiles(); loadSummary(); }}
          data-testid="btn-refresh"
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px,1fr] gap-4">
        <aside className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="folder-tree">
          <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Folder</p>
            {canWrite && (
              <button onClick={() => openNewFolder(null)} data-testid="btn-new-root-folder"
                title="Buat folder di root"
                className="p-1 rounded hover:bg-emerald-100 text-slate-500 hover:text-emerald-700">
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            <button onClick={() => setCurrentFolderId(null)}
              data-testid="folder-root"
              className={`w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-emerald-50 text-left ${
                currentFolderId === null && !isSearching ? "bg-emerald-50 border-l-4 border-emerald-500" : ""
              }`}>
              <Home className="w-4 h-4 text-emerald-700" />
              <span className="text-sm font-medium">Root</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                {rootFileCount}
              </span>
            </button>
            <FolderTree
              tree={tree} parentKey="__root__" depth={0}
              expanded={expanded} toggleFolder={toggleFolder}
              currentFolderId={currentFolderId} setCurrentFolderId={setCurrentFolderId}
              canWrite={canWrite} canDelete={canDelete}
              onNewSub={openNewFolder} onRename={openRenameFolder} onDelete={deleteFolder}
              onDownloadZip={downloadFolderZip}
            />
          </div>
        </aside>

        <main
          onDragOver={e => { e.preventDefault(); if (canWrite && !isSearching) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`bg-white border rounded-xl relative transition-colors ${dragOver ? "border-emerald-500 border-dashed bg-emerald-50/40" : "border-slate-200"}`}
          data-testid="files-panel">
          <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center gap-1 text-sm flex-wrap">
            <button onClick={() => setCurrentFolderId(null)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white text-slate-700 font-medium">
              <Home className="w-3.5 h-3.5" /> Root
            </button>
            {breadcrumb.map((b, i) => (
              <span key={b.id} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3 text-slate-400" />
                <button onClick={() => setCurrentFolderId(b.id)}
                  className={`px-2 py-1 rounded hover:bg-white ${i === breadcrumb.length - 1 ? "font-semibold text-emerald-800" : "text-slate-700"}`}
                  data-testid={`crumb-${b.id}`}>
                  {b.name}
                </button>
              </span>
            ))}
            {currentFolder && (
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => downloadFolderZip(currentFolder)}
                  data-testid="btn-download-folder-zip"
                  title="Download folder ini sebagai .zip"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">
                  <Download className="w-3.5 h-3.5" /> Download .zip
                </button>
              </div>
            )}
          </div>

          {dragOver && (
            <div className="absolute inset-0 grid place-items-center bg-emerald-500/10 backdrop-blur-sm z-10 pointer-events-none">
              <div className="bg-white rounded-xl px-6 py-4 shadow-xl border-2 border-dashed border-emerald-500">
                <Upload className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-sm font-semibold text-emerald-800">Lepas untuk upload ke folder ini</p>
              </div>
            </div>
          )}

          <div className="p-4">
            {loading && (
              <LoadingState loading data-testid="doc-files-loading" />
            )}
            {!loading && files.length === 0 && (
              <div className="text-center py-16 text-slate-400">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">
                  {isSearching ? "Tidak ada file cocok dengan filter" : "Folder ini kosong"}
                </p>
                {canWrite && !isSearching && (
                  <p className="text-xs mt-2">Drag & drop file ke sini atau klik <b>Upload File</b> di atas</p>
                )}
              </div>
            )}
            {!loading && files.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {files.map(f => <FileCard key={f.id} f={f} onOpen={() => setDetailModal(f)}
                  onPreview={() => openPreview(f)}
                  onDownload={() => downloadFile(f)}
                  showFolder={isSearching}
                  folderName={folders.find(x => x.id === f.folder_id)?.name}
                />)}
              </div>
            )}
          </div>
        </main>
      </div>

      {newFolderModal && (
        <Modal onClose={() => setNewFolderModal(null)} title="Folder Baru" testid="modal-new-folder">
          <label className="block mb-2">
            <span className="text-xs font-medium text-slate-600">Nama folder</span>
            <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              autoFocus data-testid="input-new-folder-name"
              onKeyDown={e => e.key === "Enter" && createFolder()}
              placeholder="mis. Template PKWT"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <p className="text-[11px] text-slate-500 mb-3">
            Lokasi: <b>{newFolderModal.parent_id ? folders.find(f => f.id === newFolderModal.parent_id)?.name || "-" : "Root"}</b>
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setNewFolderModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
            <button onClick={createFolder} data-testid="btn-save-folder"
              className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">Buat</button>
          </div>
        </Modal>
      )}

      {renameModal && (
        <Modal onClose={() => setRenameModal(null)} title={`Rename ${renameModal.type === "folder" ? "Folder" : "File"}`} testid="modal-rename">
          <input value={renameValue} onChange={e => setRenameValue(e.target.value)}
            autoFocus onKeyDown={e => e.key === "Enter" && doRename()}
            data-testid="input-rename"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm mb-3" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setRenameModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
            <button onClick={doRename} data-testid="btn-save-rename"
              className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">Simpan</button>
          </div>
        </Modal>
      )}

      {detailModal && (
        <FileDetailModal
          file={detailModal}
          onClose={() => setDetailModal(null)}
          allTags={summary.tags || []}
          canWrite={canWrite}
          canDelete={canDelete}
          employees={employees}
          onDownload={() => downloadFile(detailModal)}
          onPreview={() => openPreview(detailModal)}
          onToggleTag={(t) => toggleTag(detailModal, t)}
          onRename={() => openRenameFile(detailModal)}
          onDelete={() => deleteFile(detailModal)}
          onDeleteAllVersions={() => deleteFile(detailModal, true)}
          onShowVersions={() => showVersions(detailModal)}
          onUploadVersion={() => { setPendingVersionOf(detailModal); versionInputRef.current?.click(); }}
          onUpdateMeta={(patch) => updateFileMeta(detailModal, patch)}
        />
      )}

      {previewModal && (
        <PreviewModal file={previewModal.file} url={previewModal.url} onClose={closePreview} onDownload={() => downloadFile(previewModal.file)} />
      )}

      {versionsModal && (
        <Modal onClose={() => setVersionsModal(null)} title={`Riwayat Versi · ${versionsModal.file.original_filename}`} testid="modal-versions" width="max-w-2xl">
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {versionsModal.versions.map(v => (
              <VersionRow key={v.id} v={v} onDownload={() => downloadFile(v)} onDelete={() => deleteFile(v)} canDelete={canDelete} />
            ))}
            {versionsModal.versions.length === 0 && (
              <p className="text-xs text-slate-400 italic text-center py-4">Belum ada versi</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function VersionRow({ v, onDownload, onDelete, canDelete }) {
  return (
    <div className={`border rounded-lg p-3 flex items-center justify-between gap-3 ${v.is_current ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}
      data-testid={`version-row-${v.version}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">
          v{v.version}
          {v.is_current && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">CURRENT</span>}
        </p>
        <p className="text-[11px] text-slate-500">{humanSize(v.size)} · {formatTanggal(v.created_at, { denganJam: true })}</p>
        <p className="text-[10px] text-slate-400 truncate">Uploader: {v.uploaded_by_name || v.uploaded_by_email}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <button onClick={onDownload} title="Download versi ini"
          className="p-2 rounded hover:bg-white text-slate-600 hover:text-emerald-700">
          <Download className="w-4 h-4" />
        </button>
        {canDelete && (
          <button onClick={onDelete} title="Hapus versi ini"
            className="p-2 rounded hover:bg-white text-slate-400 hover:text-rose-600">
            <Download className="w-4 h-4 rotate-180" />
          </button>
        )}
      </div>
    </div>
  );
}
